/**
 * THE DASHBOARD.
 *
 * One screen replacing /dashboard/home, /dashboard/analytics, /dashboard/reports
 * and /dashboard/view-edit-export.
 *
 * ── TABS ARE STATE, NOT ROUTES ──
 * `filter.tab` drives them, so a tab is part of the view a link restores and a
 * saved view replays. Every tab that has been visited stays MOUNTED behind
 * `hidden`, so scroll position, an open drill-down and chart hover state survive
 * a switch — and, because the data layer keys per endpoint, switching back
 * refetches nothing.
 *
 * ── THE PERMISSION GATE IS THE SERVER'S ──
 * `analytics:read` is the exact permission all eight `/api/analytics/*` routes
 * are gated on. Without it the Trends tab is not rendered, `tab=trends` falls
 * back to Overview, and the data layer makes ZERO analytics requests. A tab
 * whose every request would 403 is worse than no tab.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FrontDeskChecklist } from '../../../billing/front-desk-checklist';
import { useDialogFocus } from '../../../hooks/use-dialog-focus';
import { ModalPortal } from '../../../modal/modal-portal';
import {
  DISTANCE,
  m,
  Presence,
  scrimMotion,
  T,
  useOverlayMotion,
} from '../../../../motion';
import { AnalyticsMeta } from '../../../../api/analytics-client';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../context/auth-provider';
import { useClock } from '../../../../context/clock-context';
import { User } from '../../../../interfaces/user-interfaces';
import { can, PERMISSIONS } from '../../../../auth/permissions';
import { normalizeRole } from '../../../../auth/roles';
import useScreenSize from '../../../../style-utils/use-screen-size';
import { listAllUnits, OrgUnitOption } from '../../../../api/hierarchy-client';
import toast from '../../../../utils/toast';
import { ChartDefs } from './charts/chart-defs';
import { PrintRules, PrintSource } from './print.styled';
import { svgToPng, downloadBlob } from './charts/chart-png';
import {
  RECORDS_PAGE_SIZE,
  useDashboardData,
} from './data/dashboard-data';
import { PANEL } from './data/panel-plan';
import { filterMetaFrom, Tab } from './state/filter-model';
import { useDashboardFilter } from './state/use-dashboard-filter';
import { TopBar } from './chrome/top-bar';
import { StaysCheckinAlert } from './chrome/stays-checkin-alert';
import { KpiStripView } from './chrome/kpi-strip';
import { compareLabels } from './chrome/compare-scope';
import { CompareLabelProvider } from './charts/compare-label';
import { TabBar } from './chrome/tab-bar';
import { FilterChipRow } from './chrome/filter-chip-row';
import { FilterRailFields } from './chrome/filter-rail';
import { useKeyboardShortcuts } from './chrome/use-keyboard-shortcuts';
import { ShortcutHelp } from './chrome/shortcut-help';
import { OverviewTab } from './tabs/overview-tab';
import { TrendsTab, useResults } from './tabs/trends-tab';
import { ActivityTab } from './tabs/activity-tab';
import { RecordsTab } from './tabs/records-tab';
import { ReportsTab } from './tabs/reports-tab';
import { exportView, ExportFormat } from './export/dashboard-export';
import { useDevicePrefs } from './prefs/use-device-prefs';
import { readRailOpen, writeRailOpen } from './prefs/device-prefs-store';
import { SavedViews } from './chrome/saved-views';
import {
  Body,
  Button,
  FilterCaveats,
  PanelGrid,
  DisclosureRow,
  Rail,
  RailToggle,
  Screen,
  Sheet,
  SheetHead,
  SheetScrim,
  TabPanels,
} from './dashboard-screen.styled';

/** The crossfade wrapper inside every tab panel. The panel itself stays a
 *  plain hidden-but-mounted div (state lives in it); this only fades the
 *  contents in when the tab becomes the visible one. */
const PanelFade = m.div`
  min-width: 0;
`;

/* The rail's own arrival: a short slide from the left edge with a fade. The
   grid column it occupies opens in CSS (Body). Only x/opacity — never width,
   and never a height clip, because the rail is sticky and a clipping ancestor
   would take that away. */
const RAIL_MOTION = {
  initial: { opacity: 0, x: -DISTANCE.medium },
  animate: { opacity: 1, x: 0, transition: T.enterFast },
  exit: { opacity: 0, x: -DISTANCE.small, transition: T.exit },
} as const;

/** KPI tile -> the narrowing pressing it applies. A tile that describes a
 *  narrowing the reader then has to perform by hand is the defect the desk feed
 *  already fixed once. */
const KPI_FILTER: Record<string, Record<string, unknown>> = {
  entries: { measure: 'entries', severity: null, status: null },
  incidents: { severity: 'incident' },
  people_admitted: { measure: 'people' },
  packages_logged: { measure: 'parcels_in', book: 'Parcel Log' },
  packages_pending: {
    book: 'Parcel Log',
    parcelStatus: 'pending',
    tab: 'records',
  },
  arrivals_still_open: { status: 'open' },
};

/**
 * WHICH TILE DESCRIBES THE FILTER AS IT STANDS.
 *
 * A tile is active when every field its patch would set already holds that
 * value. Several patches can be satisfied at once - both parcel tiles pin the
 * book - so the most specific match wins, which is the one actually describing
 * the view rather than a looser patch it happens to contain.
 *
 * Derived rather than remembered on purpose: the filter can reach the same state
 * from a chip, the URL or a drill, and a stored "last tile pressed" would then
 * be pointing at a view the reader has since left.
 */
export const activeKpiKey = (
  filter: Record<string, unknown>,
  map: Record<string, Record<string, unknown>> = KPI_FILTER
): string | null => {
  let best: string | null = null;
  let bestSize = 0;

  Object.keys(map).forEach(key => {
    const fields = Object.entries(map[key]);
    const satisfied = fields.every(([field, value]) => filter[field] === value);
    if (satisfied && fields.length > bestSize) {
      best = key;
      bestSize = fields.length;
    }
  });

  return best;
};

export const DashboardScreen: React.FC = () => {
  const { user, permissions }: { user: User | null | undefined; permissions: string[] } =
    useAuth();
  // Normalised, because a row still carrying a pre-020 spelling ('manager',
  // 'employee') must land on the same defaults as the canonical one — a reader
  // whose seed data is a migration behind is exactly who would never work out
  // why their dashboard opens differently from a colleague's.
  const signedInRole = normalizeRole(user?.role);
  const navigate = useNavigate();
  const canReadAnalytics = can(permissions, PERMISSIONS.ANALYTICS_READ);
  const { isMobile } = useScreenSize();

  const [sheetOpen, setSheetOpen] = useState(false);
  const sheetMotion = useOverlayMotion('sheet');
  const [helpOpen, setHelpOpen] = useState(false);
  /* The first paint is not a transition. A panel mounted with the screen shows
     at once; panels mounted or re-shown after that fade in. */
  const firstPaint = useRef(true);

  useEffect(() => {
    firstPaint.current = false;
  }, []);
  const [units, setUnits] = useState<OrgUnitOption[]>([]);
  const [exporting, setExporting] = useState(false);
  /** Tabs are mounted once visited and then kept, so switching back is free. */
  const [visited, setVisited] = useState<Set<Tab>>(new Set(['overview']));

  /* ── CUTTING THE KNOT BETWEEN meta AND THE FILTER ──
   *
   * `meta` has to reach the filter hook so it can validate against it; the
   * filter has to reach the data layer so meta can be fetched at all. Rather
   * than run two copies of either — which would double every request on the
   * screen — the descriptor is held in state and applied ONE RENDER LATE.
   *
   * That is safe because `normalise` is deliberately permissive without meta: a
   * value is assumed possible until the descriptor lands, which is also what
   * stops the first paint flashing "not available for this measure". The render
   * after it arrives re-validates everything and re-seeds the server-owned
   * defaults. */
  const [metaState, setMetaState] = useState<AnalyticsMeta | undefined>();
  const filterMeta = useMemo(
    () => (metaState ? filterMetaFrom(metaState) : undefined),
    [metaState],
  );

  const { filter, dispatch, search } = useDashboardFilter(filterMeta, signedInRole);

  /* ── THE ADVANCED RAIL IS CLOSED BY DEFAULT (QA-07) ───────────────────────
   *
   * "Collapse Advanced filters by default … keep a persistent concise filter
   * summary." The summary already exists and is unconditional — FilterChipRow,
   * rendered above the rail, names every applied narrowing and offers to remove
   * it. So folding the rail hides the THIRTY CONTROLS and hides nothing about
   * what is currently applied, which is the distinction that makes progressive
   * disclosure honest rather than merely tidier.
   *
   * Remembered per device, beside density and column choice, because whether a
   * reader wants the rail open is ergonomics — a 27-inch monitor and a 13-inch
   * laptop genuinely differ — and not a question about the data. A reader who
   * opens it once gets it open next time.
   *
   * It applies at lg+ only. Below that the filters are already a bottom sheet,
   * i.e. already disclosed on demand, and a second control for the same idea
   * would be one too many. */
  const [railOpen, setRailOpen] = useState<boolean>(() => readRailOpen());
  const toggleRail = useCallback(() => {
    setRailOpen(current => {
      writeRailOpen(!current);

      return !current;
    });
  }, []);

  /** Load a saved view: navigate to its query string. The SAME act as opening a
   *  link somebody sent, deliberately — one code path restores a filter, so a
   *  saved view and a pasted link can never disagree. Pushes, so Back returns
   *  the reader to what they were looking at. */
  const loadSavedView = useCallback(
    (saved: string) => {
      navigate({ search: saved ? `?${saved}` : '' });
    },
    [navigate]
  );

  /* DENSITY AND COLUMN VISIBILITY, per device. They are ergonomics rather than
     a question — the right answer differs between a 13-inch laptop and a 27-inch
     monitor — so they live in localStorage and never sync. The URL always wins;
     a stored preference only fills in what the address is silent about. */
  const initialSearch = useRef(
    typeof window === 'undefined' ? '' : window.location.search
  ).current;
  /* The stored density and columns fill in what the ADDRESS is silent about,
     and nothing else — the address is the whole of the rest of the state. */
  useDevicePrefs({ filter, dispatch, initialSearch });
  const data = useDashboardData(filter, {
    enabled: true,
    canReadAnalytics,
  });
  const meta = metaState;
  const results = useResults(data);
  /* The tabs whose slice list includes `batch`. Kept beside the strip rather
     than imported from filter-slices so the two cannot silently disagree about
     what "has a batch" means — this list is about what is DRAWN, that one is
     about what is FETCHED. */
  const BATCH_TABS = ['overview', 'trends', 'reports'];

  useEffect(() => {
    if (data.meta.data && data.meta.data !== metaState) setMetaState(data.meta.data);
  }, [data.meta.data, metaState]);

  /* THE UNIT LIST. One request, on mount: GET /dashboard/units returns every unit
     the caller can see and the "manager's buildings vs the admin's all" question
     is RLS on the units table, not a parameter. A failure must not take the rail
     down — the unit field falls back to free text. */
  useEffect(() => {
    let live = true;
    listAllUnits()
      .then(rows => {
        if (live) setUnits(rows);
      })
      .catch(() => undefined);

    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    setVisited(prev => (prev.has(filter.tab) ? prev : new Set(prev).add(filter.tab)));
  }, [filter.tab]);

  /* Trends is gated on the same permission the routes are. `tab=trends` in a
     link from somebody who has it must not strand a reader who does not. */
  useEffect(() => {
    if (!canReadAnalytics && filter.tab === 'trends') {
      dispatch({ type: 'setTab', tab: 'overview' }, { replace: true });
    }
  }, [canReadAnalytics, filter.tab, dispatch]);

  const unitOptions = useMemo(() => {
    const scoped =
      filter.buildingId != null
        ? units.filter(u => u.building_id === filter.buildingId)
        : units;
    const buildingName = new Map(
      (filterMeta?.buildings || []).map(b => [b.id, b.name]),
    );
    // De-duplicated on what is actually SENT. Without this, "10" in four
    // buildings is four options that produce byte-identical reports.
    const seen = new Set<string>();

    return scoped
      .map(u => ({
        value: u.unit_number,
        label:
          filter.buildingId != null
            ? u.unit_number
            : `${u.unit_number} — ${buildingName.get(u.building_id) || 'Unknown building'}`,
        archived: u.is_archived,
      }))
      .filter(option => {
        const key = `${option.value}|${option.label}`;
        if (seen.has(key)) return false;
        seen.add(key);

        return true;
      });
  }, [units, filter.buildingId, filterMeta]);

  /** The people who actually appear in this window, from the staff breakdown —
   *  never a roster of everybody. */
  const actorOptions = useMemo(() => {
    const slot = results[PANEL.staff];
    if (!slot || 'error' in slot) return [];

    return slot.items
      .filter(item => item.key)
      .map(item => ({ id: item.key, label: item.label }));
  }, [results]);

  const onExportPanelPng = useCallback(
    async (panelId: string, svg: SVGSVGElement | null) => {
      try {
        const blob = await svgToPng(svg);
        downloadBlob(`lobbero-${panelId}.png`, blob);
      } catch (error) {
        toast.error((error as Error).message || 'That chart could not be exported.');
      }
    },
    [],
  );

  const onExportPanelCsv = useCallback(
    (panelId: string) => {
      exportView('csv', {
        filter,
        filterMeta,
        data,
        results,
        panelId,
        columns: filter.columns,
      }).catch((error: Error) =>
        toast.error(error.message || 'That panel could not be exported.'),
      );
    },
    [filter, filterMeta, data, results],
  );

  const onExport = useCallback(
    (format: ExportFormat) => {
      setExporting(true);
      const id = toast.loading(`Building the ${format.toUpperCase()}…`);
      exportView(format, {
        filter,
        filterMeta,
        data,
        results,
        columns: filter.columns,
      })
        .then(() => toast.success(`${format.toUpperCase()} downloaded`, { id }))
        .catch((error: Error) =>
          toast.error(error.message || 'The export failed.', { id }),
        )
        .finally(() => setExporting(false));
    },
    [filter, filterMeta, data, results],
  );

  /**
   * ASK IQ ABOUT THIS VIEW.
   *
   * `POST /iq/ask` takes `{building_id?, question, tz}` and nothing else — there
   * is no filter-context parameter — so the context travels IN THE QUESTION,
   * composed from the filter and the figures that are actually on screen. That
   * way the answer cannot cite numbers the reader cannot see.
   *
   * `building_id` is sent only when exactly ONE building is in scope; otherwise
   * it is omitted and RLS decides, which is what the endpoint documents.
   */
  useKeyboardShortcuts({
    onTab: tab => dispatch({ type: 'setTab', tab }),
    onFilters: () => setSheetOpen(open => !open),
    onExport: () => onExport('csv'),
    onEscape: () => {
      if (filter.drill) dispatch({ type: 'clearDrill' });
      else setSheetOpen(false);
    },
    onHelp: () => setHelpOpen(true),
    hidden: canReadAnalytics ? [] : ['trends'],
  });

  const tabProps = {
    filter,
    filterMeta,
    meta,
    data,
    dispatch,
    isMobile,
    onExportPanelPng,
    onExportPanelCsv,
  };

  /* The address of exactly this view. Printed, never shown: every filter on this
     screen lives in the URL, so a sheet of paper that carries it is a sheet
     somebody else can reproduce and check. Read from `location` rather than
     rebuilt, so it is the link that is actually in the address bar. */
  const printUrl =
    typeof window === 'undefined'
      ? ''
      : `${window.location.origin}${window.location.pathname}${window.location.search}`;

  /* THE COUNT IS OF A PAGE, AND SAYS SO.
   *
   * `rows.length` is the length of ONE page — `fetchRecords` sends limit 50 —
   * so a 3,000-row result rendered as "50 records", and paging to page 2 said
   * "50 records" again. The response carries no total, so the honest options
   * were to invent one or to stop presenting a page length as a result size.
   * `pageOnly` tells the chip row to say "50 on this page" instead. */
  const recordCount = data.records.data
    ? {
        total: data.records.data.rows.length,
        capped: data.records.data.truncated,
        compare: null,
        pageOnly: data.records.data.rows.length >= RECORDS_PAGE_SIZE,
      }
    : data.activity.data
      ? {
          total: data.activity.data.total,
          capped: data.activity.data.totalCapped,
          compare: null,
        }
      : null;

  /* HIDDEN-BUT-MOUNTED, AND CROSSFADED. The tabpanel div keeps `hidden` (that
     is what preserves scroll, drill and hover state, and what the tab bar's
     aria-controls points at); the fade wrapper inside animates opacity only —
     no x travel, because the panels stack and a transform on a panel this
     large is layout cost for nothing. */
  const panel = (tab: Tab, node: React.ReactNode) =>
    visited.has(tab) ? (
      <div
        key={tab}
        id={`dash-panel-${tab}`}
        role="tabpanel"
        aria-labelledby={`dash-tab-${tab}`}
        hidden={filter.tab !== tab}
      >
        <PanelFade
          initial={firstPaint.current ? false : { opacity: 0 }}
          animate={{ opacity: filter.tab === tab ? 1 : 0 }}
          transition={T.enterFast}
        >
          {node}
        </PanelFade>
      </div>
    ) : null;

  /**
   * THE PHONE FILTER SHEET IS A REAL MODAL, SO IT BEHAVES LIKE ONE.
   *
   * It declared `role="dialog" aria-modal="true"` and had none of the
   * behaviour: focus stayed on the Filters button behind the scrim, Tab walked
   * the dashboard underneath, Escape did nothing, and closing left focus at the
   * top of the document. A sheet that covers the screen and claims to be modal
   * has to trap and restore focus, or the claim is just an attribute.
   */
  const sheetRef = useDialogFocus<HTMLDivElement>(sheetOpen, () =>
    setSheetOpen(false)
  );

  const scopeForCaption =
    data.batch.data?.scope || data.overview.data?.scope || meta?.scope;

  /**
   * THE CLOCK THE ANALYTICS ANSWER IS ON — from the answer itself.
   *
   * Every table on this screen used to format its instants in "the one zone in
   * scope", and silently in UTC the moment the buildings spanned two. Under
   * R-T12 an owner's or a manager's screen is on ONE clock — theirs — which the
   * server has already applied and names on every response (`tz`,
   * `tz_source`). So each tab takes its clock from ITS OWN response and says
   * which it is; this one is the analytics answer's, for the caption under the
   * top bar.
   */
  const analyticsAnswer = data.batch.data || data.overview.data;
  const answerClock = useClock({
    buildingId: filter.buildingId,
    response: analyticsAnswer
      ? {
          tz: analyticsAnswer.tz || analyticsAnswer.window?.tz,
          tz_source: analyticsAnswer.tz_source,
        }
      : null,
  });

  return (
    /* WHAT THE MUTED SECOND SERIES ON EVERY PANEL IS CALLED. With a second
       scope chosen it is that place's name; otherwise it is the earlier period.
       A provider rather than a prop threaded through five tabs into fourteen
       panels — see charts/compare-label.tsx. */
    <CompareLabelProvider
      scopeName={filter.compareScope ? compareLabels(filter, filterMeta).compare : null}
    >
      <Screen>
        <FrontDeskChecklist />
        {/* Every gradient and pattern this screen uses, once. */}
        <ChartDefs />

        {/* WHAT Ctrl+P PRODUCES. Global, because the chrome that has to disappear
          on paper — the sidebar, the notification dock, the IQ launcher — lives
          above this component. */}
        <PrintRules />
        <PrintSource url={printUrl} />

        <TopBar
          filter={filter}
          meta={filterMeta}
          dispatch={dispatch}
          window={data.batch.data?.window || data.overview.data?.window}
          compareWindow={
            data.batch.data?.compareWindow || data.overview.data?.compareWindow
          }
          scope={scopeForCaption}
          curSpanMs={data.overview.data?.curSpanMs}
          prevSpanMs={data.overview.data?.prevSpanMs}
          likeForLike={data.overview.data?.likeForLike}
          canReadAnalytics={canReadAnalytics}
          exportDisabled={exporting}
          onExport={onExport}
          onOpenFilters={() => setSheetOpen(true)}
          answerClock={analyticsAnswer ? answerClock : undefined}
        />

        {/* Lobbero Stays plan S3: guests arriving soon without a finished
            check-in. Renders nothing unless Stays is on, the reader holds
            stays:read and the count is above zero. */}
        <StaysCheckinAlert buildingId={filter.buildingId} />

        {canReadAnalytics ? (
          <KpiStripView
            overview={data.overview.data}
            loading={data.overview.loading}
            /* NO SPARKLINE ON A TAB THAT DOES NOT REFETCH THE BATCH.
                The strip is sticky and always visible; the batch is only asked
                for on the tabs that draw panels. Handing the last batch to the
                strip on Records or Activity would draw a trend line for the
                PREVIOUS filter under a figure for the current one — two
                different questions in one tile. A tile without its sparkline is
                still a figure. */
            results={BATCH_TABS.includes(filter.tab) ? results : {}}
            activeKey={activeKpiKey(filter as unknown as Record<string, unknown>)}
            onSelect={key => {
              const patch = KPI_FILTER[key];
              if (patch) dispatch({ type: 'patch', patch });
            }}
            compare={data.overviewB.data}
            compareLoading={data.overviewB.loading}
            scopeLabels={
              filter.compareScope ? compareLabels(filter, filterMeta) : undefined
            }
          />
        ) : null}

        <TabBar
          active={filter.tab}
          hidden={canReadAnalytics ? [] : ['trends']}
          onChange={tab => dispatch({ type: 'setTab', tab })}
        />

        <FilterChipRow
          filter={filter}
          meta={filterMeta}
          onRemove={patch => dispatch({ type: 'clearChip', patch })}
          onClearAll={() => dispatch({ type: 'clearAll' })}
          count={recordCount}
        />

        {/* THE TWO DISCLOSURE CONTROLS, on the row under the summary they act on
            (QA-07). Together they are the whole of the progressive-disclosure
            change the reader can see: fold the thirty specialist controls away,
            and keep the filter you assembled. */}
        <DisclosureRow>
          <RailToggle
            type="button"
            aria-expanded={railOpen}
            aria-controls="dashboard-filter-rail"
            onClick={toggleRail}
            data-testid="rail-toggle"
          >
            {railOpen ? 'Hide advanced filters' : 'Advanced filters'}
          </RailToggle>
          <SavedViews search={search} onLoad={loadSavedView} />
        </DisclosureRow>

        {/* ── WHAT A CHIP DID NOT DO ──
            Directly under the chips, because that is where the reader was told
            the filter is on. The server names each filter the figures could not
            honour — an encrypted name, a direction that is a property of an
            event rather than a column — and a chip that narrowed nothing while
            looking applied is the one failure on this screen where both numbers
            look right. Empty is the normal case and renders nothing. */}
        {data.overview.data?.filterNotes?.length ? (
          <FilterCaveats data-testid="filter-caveats">
            {data.overview.data.filterNotes.map(caveat => (
              <li key={caveat}>{caveat}</li>
            ))}
          </FilterCaveats>
        ) : null}

        {/* WHAT THE MUTED SECOND SERIES IS CALLED, for every panel below. See
          charts/compare-label.tsx: with a second scope chosen it is that place's
          name, otherwise it is the earlier period. */}
        <Body railOpen={railOpen}>
          {/* NOT a Collapse: the rail is a sticky column and Collapse clips
              height with overflow hidden, which would end the stickiness. The
              column opens in CSS; the rail slides in beside the panels. */}
          <Presence initial={false}>
            {railOpen ? (
              <Rail
                key="rail"
                {...RAIL_MOTION}
                id="dashboard-filter-rail"
                aria-label="Advanced filters"
                data-testid="filter-rail"
              >
                <FilterRailFields
                  filter={filter}
                  meta={filterMeta}
                  dispatch={dispatch}
                  canReadAnalytics={canReadAnalytics}
                  actorOptions={actorOptions}
                  unitOptions={unitOptions}
                />
              </Rail>
            ) : null}
          </Presence>

          <TabPanels>
            {panel('overview', <OverviewTab {...tabProps} />)}
            {canReadAnalytics ? panel('trends', <TrendsTab {...tabProps} />) : null}
            {panel(
              'activity',
              <ActivityTab
                filter={filter}
                dispatch={dispatch}
                data={data}
              />,
            )}
            {panel(
              'records',
              <PanelGrid data-testid="panel-grid">
                <RecordsTab
                  filter={filter}
                  dispatch={dispatch}
                  records={data.records}
                />
              </PanelGrid>,
            )}
            {panel(
              'reports',
              <ReportsTab
                filter={filter}
                filterMeta={filterMeta}
                dispatch={dispatch}
                data={data}
                isMobile={isMobile}
                onExportPanelPng={onExportPanelPng}
              />,
            )}
          </TabPanels>
        </Body>

        {/* THE PHONE SHEET. Only this is portalled — the desk rail is an ordinary
          sticky column. It was written inline and declared portalled; an inline
          fixed overlay under an animated ancestor is the stacking-context trap
          ModalPortal exists for. The scrim fades, the sheet rises from the
          bottom edge, and both leave the way they came. */}
        <Presence>
          {sheetOpen ? (
            <ModalPortal key="filter-sheet">
              <SheetScrim {...scrimMotion} onClick={() => setSheetOpen(false)} />
              <Sheet
                {...sheetMotion}
                ref={sheetRef}
                tabIndex={-1}
                role="dialog"
                aria-modal="true"
                aria-label="Filters"
                data-testid="filter-sheet"
              >
                <SheetHead>
                  <strong>Filters</strong>
                  <Button type="button" onClick={() => setSheetOpen(false)}>
                    Done
                  </Button>
                </SheetHead>
                <FilterRailFields
                  filter={filter}
                  meta={filterMeta}
                  dispatch={dispatch}
                  canReadAnalytics={canReadAnalytics}
                  actorOptions={actorOptions}
                  unitOptions={unitOptions}
                />
              </Sheet>
            </ModalPortal>
          ) : null}
        </Presence>
        {/* Mounted always; `open` drives it so it can animate out. */}
        <ShortcutHelp open={helpOpen} onClose={() => setHelpOpen(false)} />
    </Screen>
    </CompareLabelProvider>
  );
};

export default DashboardScreen;
